import React, { useState, useEffect } from 'react';
import { 
  Users, Loader2, MapPin, Clock, User, Check, LogOut, Star,
  Filter
} from 'lucide-react';
import { studentClubsAPI } from '../../services/api';

const CATEGORIES = {
  sport: { name: 'Sport', icon: '⚽', color: '#22c55e', gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' },
  art: { name: "San'at", icon: '🎨', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  science: { name: 'Fan', icon: '🔬', color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
  language: { name: 'Til', icon: '🌍', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
  tech: { name: 'Texnologiya', icon: '💻', color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' },
  music: { name: 'Musiqa', icon: '🎵', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
  other: { name: 'Boshqa', icon: '📚', color: '#64748b', gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)' },
};

const StudentClubs = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [showMyClubs, setShowMyClubs] = useState(false);

  useEffect(() => {
    fetchClubs();
  }, [filterCategory]);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      
      const response = await studentClubsAPI.getAll(params);
      setClubs(response.data.clubs || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (clubId) => {
    setActionLoading(clubId);
    try {
      await studentClubsAPI.join(clubId);
      fetchClubs();
    } catch (error) {
      alert(error.response?.data?.error || 'Xatolik yuz berdi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeave = async (clubId) => {
    if (window.confirm("Rostdan ham to'garakdan chiqmoqchimisiz?")) {
      setActionLoading(clubId);
      try {
        await studentClubsAPI.leave(clubId);
        fetchClubs();
      } catch (error) {
        alert(error.response?.data?.error || 'Xatolik yuz berdi');
      } finally {
        setActionLoading(null);
      }
    }
  };

  const filteredClubs = showMyClubs 
    ? clubs.filter(club => club.is_member)
    : clubs;

  const myClubsCount = clubs.filter(c => c.is_member).length;

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Users size={28} /> To'garaklar</h1>
          <p>Qiziqarli mashg'ulotlarga qo'shiling</p>
        </div>
      </header>

      {/* Statistika */}
      <div className="clubs-hero">
        <div className="clubs-hero-content">
          <h2>O'z qobiliyatingizni rivojlantiring!</h2>
          <p>To'garaklarga a'zo bo'ling va yangi do'stlar orttiring</p>
          <div className="clubs-stats-mini">
            <div className="stat-mini">
              <span className="stat-number">{clubs.length}</span>
              <span className="stat-text">to'garak</span>
            </div>
            <div className="stat-mini">
              <span className="stat-number">{myClubsCount}</span>
              <span className="stat-text">a'zoligim</span>
            </div>
          </div>
        </div>
        <div className="clubs-hero-illustration">
          🎯
        </div>
      </div>

      {/* Filterlar */}
      <div className="clubs-filters">
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${!showMyClubs ? 'active' : ''}`}
            onClick={() => setShowMyClubs(false)}
          >
            Barchasi
          </button>
          <button 
            className={`filter-tab ${showMyClubs ? 'active' : ''}`}
            onClick={() => setShowMyClubs(true)}
          >
            <Star size={16} />
            Mening to'garaklarim ({myClubsCount})
          </button>
        </div>
        <div className="category-filters">
          <button 
            className={`category-chip ${filterCategory === '' ? 'active' : ''}`}
            onClick={() => setFilterCategory('')}
          >
            <Filter size={14} />
            Barchasi
          </button>
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              className={`category-chip ${filterCategory === key ? 'active' : ''}`}
              onClick={() => setFilterCategory(key)}
              style={{ '--chip-color': cat.color }}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* To'garaklar */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : filteredClubs.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <p>{showMyClubs ? "Siz hali hech qaysi to'garakka a'zo emassiz" : "To'garaklar topilmadi"}</p>
        </div>
      ) : (
        <div className="student-clubs-grid">
          {filteredClubs.map(club => {
            const cat = CATEGORIES[club.category] || CATEGORIES.other;
            const isLoading = actionLoading === club.id;
            
            return (
              <div 
                key={club.id} 
                className={`student-club-card ${club.is_member ? 'member' : ''}`}
                style={{ '--club-gradient': cat.gradient, '--club-color': cat.color }}
              >
                <div className="student-club-header">
                  <div className="student-club-icon">{cat.icon}</div>
                  {club.is_member && (
                    <div className="member-badge">
                      <Check size={12} />
                      A'zo
                    </div>
                  )}
                </div>
                
                <div className="student-club-body">
                  <span className="student-club-category">{cat.name}</span>
                  <h3 className="student-club-name">{club.name}</h3>
                  {club.description && (
                    <p className="student-club-desc">{club.description}</p>
                  )}
                  
                  <div className="student-club-details">
                    {club.teacher_name && (
                      <div className="detail-item">
                        <User size={14} />
                        <span>{club.teacher_name}</span>
                      </div>
                    )}
                    {club.schedule && (
                      <div className="detail-item">
                        <Clock size={14} />
                        <span>{club.schedule}</span>
                      </div>
                    )}
                    {club.location && (
                      <div className="detail-item">
                        <MapPin size={14} />
                        <span>{club.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="student-club-footer">
                  <div className="members-info">
                    <div className="members-bar">
                      <div 
                        className="members-fill" 
                        style={{ width: `${(club.members_count / club.max_members) * 100}%` }}
                      />
                    </div>
                    <span className="members-text">
                      {club.members_count} / {club.max_members} a'zo
                    </span>
                  </div>
                  
                  {club.is_member ? (
                    <button 
                      className="btn btn-leave"
                      onClick={() => handleLeave(club.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="spinner" size={16} /> : <LogOut size={16} />}
                      Chiqish
                    </button>
                  ) : club.is_full ? (
                    <button className="btn btn-disabled" disabled>
                      To'lgan
                    </button>
                  ) : (
                    <button 
                      className="btn btn-join"
                      onClick={() => handleJoin(club.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="spinner" size={16} /> : <Check size={16} />}
                      Qo'shilish
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentClubs;
